import React, { useEffect, useState, useCallback } from 'react';
import { contentService } from '../../services/apiService';
import { toast } from 'react-toastify';
import {
  FiFilter,
  FiEye,
  FiDownload,
  FiX,
  FiEdit3,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiChevronUp,
  FiBarChart2,
  FiList,
  FiUser,
  FiRefreshCw,
  FiCalendar,
} from 'react-icons/fi';
import { useT } from '../../utils/i18n';
import { useAuthStore } from '../../utils/store';
import { formatISTDate, formatISTTime, formatISTDateTime, getISTPresets, istLocalToISO } from '../../utils/dateUtils';

// ─── Shared: camelCase → Title Case ──────────────────────────────────────────
function toLabel(type = '') {
  return type
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/([0-9]+)/g, ' $1')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Line-level LCS diff ──────────────────────────────────────────────────────
function lineDiff(original = '', edited = '') {
  const a = (original || '').split('\n');
  const b = (edited   || '').split('\n');
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i+1][j+1] + 1 : Math.max(dp[i+1][j], dp[i][j+1]);
  const out = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ type: 'same', value: a[i] }); i++; j++; }
    else if (dp[i+1][j] >= dp[i][j+1]) { out.push({ type: 'del', value: a[i] }); i++; }
    else { out.push({ type: 'add', value: b[j] }); j++; }
  }
  while (i < n) { out.push({ type: 'del', value: a[i++] }); }
  while (j < m) { out.push({ type: 'add', value: b[j++] }); }
  return out;
}

// ─── GitHub-style diff view ───────────────────────────────────────────────────
const CONTEXT_LINES = 3;

const GitHubLineDiff = ({ original, edited }) => {
  const hunks  = lineDiff(original, edited);
  const changed = new Set(
    hunks.reduce((acc, h, idx) => { if (h.type !== 'same') acc.push(idx); return acc; }, [])
  );

  if (changed.size === 0) {
    return (
      <div className="rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-400">
        No differences — AI output and edited output are identical.
      </div>
    );
  }

  const showLine = new Set();
  changed.forEach((idx) => {
    for (let k = Math.max(0, idx - CONTEXT_LINES); k <= Math.min(hunks.length - 1, idx + CONTEXT_LINES); k++)
      showLine.add(k);
  });

  const visible = [];
  let collapsed = false, collapseStart = -1;
  hunks.forEach((hunk, idx) => {
    if (showLine.has(idx)) {
      if (collapsed) { visible.push({ type: 'collapse', count: idx - collapseStart }); collapsed = false; }
      visible.push({ ...hunk, idx });
    } else {
      if (!collapsed) { collapsed = true; collapseStart = idx; }
    }
  });
  if (collapsed) visible.push({ type: 'collapse', count: hunks.length - collapseStart });

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden text-xs font-mono">
      <div className="flex items-center gap-4 px-3 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-sans">
        <span className="flex items-center gap-1.5 text-red-600 font-semibold">
          <span className="inline-block w-3.5 text-center">−</span> AI Output (removed)
        </span>
        <span className="flex items-center gap-1.5 text-green-700 font-semibold">
          <span className="inline-block w-3.5 text-center">+</span> Edited Output (added)
        </span>
      </div>
      <div className="overflow-x-auto">
        {visible.map((item, idx) =>
          item.type === 'collapse' ? (
            <div key={`c-${idx}`} className="px-3 py-1 bg-gray-50 text-gray-400 text-[11px] border-y border-gray-100 select-none">
              ···  {item.count} unchanged {item.count === 1 ? 'line' : 'lines'}  ···
            </div>
          ) : (
            <div
              key={`l-${idx}`}
              className={`flex min-h-[1.4rem] ${
                item.type === 'del' ? 'bg-red-50' : item.type === 'add' ? 'bg-green-50' : 'bg-white'
              }`}
            >
              <span className={`w-5 flex-shrink-0 text-center select-none pt-0.5 ${
                item.type === 'del' ? 'text-red-400' : item.type === 'add' ? 'text-green-600' : 'text-gray-300'
              }`}>
                {item.type === 'del' ? '−' : item.type === 'add' ? '+' : ' '}
              </span>
              <span className={`flex-1 px-1 py-0.5 whitespace-pre-wrap break-words leading-5 ${
                item.type === 'del' ? 'text-red-800' : item.type === 'add' ? 'text-green-800' : 'text-gray-700'
              }`}>
                {item.value || ' '}
              </span>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ─── Section label ────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">{children}</p>
);

// ─── ViewModal ────────────────────────────────────────────────────────────────
// canViewTeamHistory: true for admins/superadmins (all users) AND managers
// (managers + editors only) — see the History component below for how it's computed.
const ViewModal = ({ content, onClose, t, canViewTeamHistory }) => {
  const editEntry    = content.editHistory?.[0];
  const hasEdits     = !!editEntry;
  const aiOutput     = hasEdits ? editEntry.originalContent : content.outputs?.primaryOutput;
  const editedOutput = hasEdits ? editEntry.editedContent   : null;
  const lastEdited   = hasEdits ? editEntry.editedAt        : null;

  const [activeTab, setActiveTab] = useState(hasEdits ? 'edited' : 'ai');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-3 py-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-gray-900">{toLabel(content.contentType)}</h2>
              {content.isEdited && (
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  <FiEdit3 size={10} />{t('history.editedBadge')}
                </span>
              )}
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                content.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
              }`}>
                {content.status === 'completed' ? t('history.completed') : t('history.failed')}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {formatISTDateTime(content.createdAt)}
              {content.tokenUsage?.totalTokens ? ` · ${content.tokenUsage.totalTokens} tokens` : ''}
              {content.costUSD != null ? ` · $${content.costUSD.toFixed(4)}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="ml-4 text-gray-400 hover:text-gray-600 flex-shrink-0"><FiX size={20} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row min-h-0">
          {/* Left: input + settings */}
          <div className="lg:w-2/5 flex flex-col border-b lg:border-b-0 lg:border-r border-gray-200 min-h-0">
            <div className="px-5 pt-4 pb-2 flex-shrink-0"><SectionLabel>{t('history.input')}</SectionLabel></div>
            <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-4">
              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                {content.inputContent || <span className="italic text-gray-400">—</span>}
              </p>
              {(content.tone || content.duration || content.aiProvider || content.customInstructions || content.outputs?.metadata?.wordCount) && (
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">{t('history.settingsUsed')}</p>
                  <div className="space-y-1.5">
                    {content.tone && (
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-400 w-24 flex-shrink-0">{t('history.toneLabel')}</span>
                        <span className="text-xs font-semibold bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full capitalize">{content.tone}</span>
                      </div>
                    )}
                    {content.duration > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-400 w-24 flex-shrink-0">{t('history.durationLabel')}</span>
                        <span className="text-xs text-gray-700">{content.duration} {t('history.wordsUnit')}</span>
                      </div>
                    )}
                    {content.outputs?.metadata?.wordCount > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-400 w-24 flex-shrink-0">{t('history.wordCountLabel')}</span>
                        <span className="text-xs text-gray-700">{content.outputs.metadata.wordCount} {t('history.wordsUnit')}</span>
                      </div>
                    )}
                    {canViewTeamHistory && content.aiProvider && (
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-400 w-24 flex-shrink-0">{t('history.providerLabel')}</span>
                        <span className="text-xs text-gray-700 capitalize">{content.aiProvider}</span>
                      </div>
                    )}
                    {content.customInstructions && (
                      <div className="flex items-start gap-2">
                        <span className="text-[11px] text-gray-400 w-24 flex-shrink-0 pt-0.5">{t('history.customInstructionsLabel')}</span>
                        <span className="text-xs text-gray-700 break-words">{content.customInstructions}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: AI / Edited / Diff tabs */}
          <div className="lg:w-3/5 flex flex-col min-h-0">
            <div className="px-5 pt-4 pb-0 flex-shrink-0">
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs w-fit">
                <button onClick={() => setActiveTab('ai')} className={`px-3 py-1.5 font-medium transition-colors ${activeTab === 'ai' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {t('history.aiTab')}
                </button>
                <button onClick={() => setActiveTab('edited')} disabled={!hasEdits} className={`px-3 py-1.5 font-medium transition-colors disabled:opacity-40 ${activeTab === 'edited' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {t('history.editedTab')}
                </button>
                {hasEdits && (
                  <button onClick={() => setActiveTab('diff')} className={`px-3 py-1.5 font-medium transition-colors ${activeTab === 'diff' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    Differences
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {activeTab === 'ai' && (
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {aiOutput || <span className="italic text-gray-400">—</span>}
                </p>
              )}
              {activeTab === 'edited' && (
                hasEdits ? (
                  <>
                    {lastEdited && <p className="text-[11px] text-gray-400 mb-3">{t('history.lastEditedOn')} {formatISTDateTime(lastEdited)}</p>}
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{editedOutput}</p>
                  </>
                ) : <p className="text-sm text-gray-400 italic">{t('history.noEditYet')}</p>
              )}
              {activeTab === 'diff' && hasEdits && (
                <>
                  {lastEdited && <p className="text-[11px] text-gray-400 mb-3">{t('history.lastEditedOn')} {formatISTDateTime(lastEdited)}</p>}
                  <GitHubLineDiff original={aiOutput} edited={editedOutput} />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 flex-shrink-0 flex justify-end">
          <button onClick={onClose} className="btn btn-primary btn-sm">{t('history.close')}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Pagination helper ────────────────────────────────────────────────────────
function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.add(i);
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
    result.push(sorted[i]);
  }
  return result;
}

// ─── Details tab ─────────────────────────────────────────────────────────────
const DetailsTab = ({ t, canViewTeamHistory }) => {
  const [contents, setContents]     = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
  const [filters, setFilters]       = useState({
    contentType: '', status: '', isEdited: '', userName: '', search: '', page: 1, limit: 10,
  });
  const [selectedContent, setSelectedContent] = useState(null);

  useEffect(() => { fetchHistory(); }, [filters]);

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      const result = await contentService.getHistory(filters);
      setContents(result.data ?? result);
      if (result.pagination) setPagination(result.pagination);
    } catch {
      toast.error(t('toast.historyFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handleViewContent = async (id) => {
    try {
      const content = await contentService.getContentById(id);
      setSelectedContent(content);
    } catch {
      toast.error(t('toast.contentFailed'));
    }
  };

  const handleDownload = (content) => {
    const a = document.createElement('a');
    a.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(content.outputs?.primaryOutput || ''));
    a.setAttribute('download', `${content.contentType}_${content._id}.txt`);
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-5">
      {/* Filters card */}
      <div className="card">
        <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiFilter size={16} />{t('history.filters')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Type */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('history.contentType')}</label>
            <select name="contentType" value={filters.contentType} onChange={handleFilterChange} className="input text-sm">
              <option value="">{t('history.allTypes')}</option>
              {['anchorVisual','anchorByte','anchorPackage','videoToScript','breakingPlates','breakingPoints',
                'tops','newsBulletin','scrollings','voiceOver','speedNews','headlinesSlugs',
                'breakingNewsPresentation','anchorBgTitles','promoScript','conspiracyTheory'].map((k) => (
                <option key={k} value={k}>{toLabel(k)}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('history.status')}</label>
            <select name="status" value={filters.status} onChange={handleFilterChange} className="input text-sm">
              <option value="">{t('history.allStatus')}</option>
              <option value="completed">{t('history.completed')}</option>
              <option value="failed">{t('history.failed')}</option>
            </select>
          </div>

          {/* User name (admin sees everyone, manager sees managers+editors) */}
          {canViewTeamHistory && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">User Name</label>
              <div className="relative">
                <FiUser size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="userName"
                  value={filters.userName}
                  onChange={handleFilterChange}
                  placeholder="Search by name…"
                  className="input text-sm pl-8"
                />
              </div>
            </div>
          )}
        </div>

        {/* Toggle: Has Edited Output */}
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <div
              onClick={() => setFilters((prev) => ({ ...prev, isEdited: prev.isEdited === 'true' ? '' : 'true', page: 1 }))}
              className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${filters.isEdited === 'true' ? 'bg-primary-500' : 'bg-gray-200'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${filters.isEdited === 'true' ? 'translate-x-4' : ''}`} />
            </div>
            <span className="text-sm font-medium text-gray-700">Has Edited Output</span>
            {filters.isEdited === 'true' && (
              <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                <FiEdit3 size={10} /> Edited only
              </span>
            )}
          </label>
        </div>
      </div>

      {/* Table card */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-primary-500" />
          </div>
        ) : contents.length === 0 ? (
          <div className="text-center py-12"><p className="text-gray-500">{t('history.noContent')}</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {canViewTeamHistory && <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">{t('history.user')}</th>}
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">{t('history.type')}</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">{t('history.toneLabel')}</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">{t('history.status')}</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">{t('history.tokens')}</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">{t('history.cost')}</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">{t('history.date')}</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">{t('history.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {contents.map((content) => (
                  <tr key={content._id} className="border-b border-gray-100 hover:bg-gray-50">
                    {canViewTeamHistory && (
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-gray-900">{content.userId?.name || '—'}</p>
                        <p className="text-xs text-gray-400">{content.userId?.email}</p>
                      </td>
                    )}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="badge badge-primary">{toLabel(content.contentType)}</span>
                        {content.isEdited && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                            <FiEdit3 size={9} />{t('history.editedBadge')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {content.tone
                        ? <span className="text-xs font-medium bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full capitalize">{content.tone}</span>
                        : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`badge ${content.status === 'completed' ? 'badge-success' : 'badge-danger'}`}>
                        {content.status === 'completed' ? t('history.completed') : t('history.failed')}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{content.tokenUsage?.totalTokens ?? '—'}</td>
                    <td className="py-3 px-4 text-sm">${content.costUSD?.toFixed(4) ?? '—'}</td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-700">{formatISTDate(content.createdAt)}</p>
                      <p className="text-xs text-gray-400">{formatISTTime(content.createdAt)}</p>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleViewContent(content._id)} className="btn btn-ghost btn-sm" title="View"><FiEye size={16} /></button>
                        <button onClick={() => handleDownload(content)} className="btn btn-ghost btn-sm" title="Download"><FiDownload size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && pagination.total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 mt-2 border-t border-gray-100">
            <div className="flex items-center gap-3 text-sm text-gray-500 flex-shrink-0">
              <span>
                {t('history.pagination.showing')}{' '}
                <span className="font-medium text-gray-700">{((pagination.page - 1) * pagination.limit) + 1}</span>
                {' '}–{' '}
                <span className="font-medium text-gray-700">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                {' '}{t('history.pagination.of')}{' '}
                <span className="font-medium text-gray-700">{pagination.total}</span>
                {' '}{t('history.pagination.results')}
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-400">{t('history.pagination.perPage')}:</span>
                <select
                  value={filters.limit}
                  onChange={(e) => setFilters((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
                >
                  {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
            {pagination.pages > 1 && (
              <div className="flex items-center gap-1">
                <button onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <FiChevronLeft size={14} />
                  <span className="hidden sm:inline">{t('history.pagination.prev')}</span>
                </button>
                {getPageNumbers(pagination.page, pagination.pages).map((p, idx) =>
                  p === '...' ? (
                    <span key={`e-${idx}`} className="px-1.5 py-1.5 text-sm text-gray-400 select-none">…</span>
                  ) : (
                    <button key={p} onClick={() => setFilters((prev) => ({ ...prev, page: p }))}
                      className={`min-w-[32px] px-2 py-1.5 rounded-lg text-sm font-medium border transition-colors ${p === pagination.page ? 'bg-primary-500 border-primary-500 text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {p}
                    </button>
                  )
                )}
                <button onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <span className="hidden sm:inline">{t('history.pagination.next')}</span>
                  <FiChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedContent && (
        <ViewModal content={selectedContent} onClose={() => setSelectedContent(null)} t={t} canViewTeamHistory={canViewTeamHistory} />
      )}
    </div>
  );
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub }) => (
  <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

// datePresets moved to dateUtils.js — getISTPresets()

// ─── Admin Report tab ─────────────────────────────────────────────────────────
const ReportTab = ({ t }) => {
  const presets = getISTPresets();
  const [filters, setFilters]   = useState({ dateFrom: presets[3].from, dateTo: presets[3].to, contentType: '' }); // default: Last 30 days
  const [activePreset, setPreset] = useState('Last 30 days');
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [sortBy, setSortBy]     = useState('totalCostUSD'); // totalCount | totalCostUSD | totalTokens

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        ...filters,
        dateFrom: istLocalToISO(filters.dateFrom),
        dateTo:   istLocalToISO(filters.dateTo),
      };
      const data = await contentService.getReport(params);
      setReport(data);
    } catch {
      setError('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const applyPreset = (preset) => {
    setPreset(preset.label);
    setFilters((prev) => ({ ...prev, dateFrom: preset.from, dateTo: preset.to }));
  };

  const toggleExpand = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const fmt = (n) => n?.toLocaleString() ?? '0';
  const fmtCost = (n) => `$${(n || 0).toFixed(4)}`;
  const fmtK  = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n ?? 0);

  const sortedUsers = report
    ? [...report.users].sort((a, b) => (b[sortBy] ?? 0) - (a[sortBy] ?? 0))
    : [];

  const SortBtn = ({ field, label }) => (
    <button
      onClick={() => setSortBy(field)}
      className={`text-left text-xs font-semibold uppercase tracking-wide transition-colors ${
        sortBy === field ? 'text-primary-600' : 'text-gray-500 hover:text-gray-800'
      }`}
    >
      {label}{sortBy === field && ' ↓'}
    </button>
  );

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <FiCalendar size={16} /> Date Range & Filters
          </h2>
          <button onClick={fetchReport} disabled={loading}
            className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50">
            <FiRefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {/* Quick presets */}
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button key={p.label} onClick={() => applyPreset(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                activePreset === p.label
                  ? 'bg-primary-500 border-primary-500 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom date-time + content type — values are IST local (YYYY-MM-DDTHH:MM) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From (IST)</label>
            <input type="datetime-local" value={filters.dateFrom}
              onChange={(e) => { setPreset('Custom'); setFilters((f) => ({ ...f, dateFrom: e.target.value })); }}
              className="input text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To (IST)</label>
            <input type="datetime-local" value={filters.dateTo}
              onChange={(e) => { setPreset('Custom'); setFilters((f) => ({ ...f, dateTo: e.target.value })); }}
              className="input text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Content Type</label>
            <select value={filters.contentType} onChange={(e) => setFilters((f) => ({ ...f, contentType: e.target.value }))} className="input text-sm">
              <option value="">All Types</option>
              {['anchorVisual','anchorByte','anchorPackage','videoToScript','breakingPlates','breakingPoints',
                'tops','newsBulletin','scrollings','voiceOver','speedNews','headlinesSlugs',
                'breakingNewsPresentation','anchorBgTitles','promoScript','conspiracyTheory'].map((k) => (
                <option key={k} value={k}>{toLabel(k)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-100 rounded-xl px-4 py-3">
          <FiX size={14} />{error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-primary-500" />
        </div>
      )}

      {/* Results */}
      {!loading && report && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Users" value={report.users.length} />
            <StatCard label="Total Generations" value={fmt(report.totals.count)} />
            <StatCard label="Total Cost" value={fmtCost(report.totals.costUSD)} sub={`${fmtK(report.totals.tokens)} tokens`} />
            <StatCard label="Edited Outputs" value={fmt(report.totals.edited)}
              sub={report.totals.count ? `${((report.totals.edited / report.totals.count) * 100).toFixed(1)}% edit rate` : ''} />
          </div>

          {/* Per-user table */}
          {report.users.length === 0 ? (
            <div className="card text-center py-12 text-gray-400 text-sm">No data for the selected period.</div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-gray-900">User-wise Breakdown</h3>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  Sort by:
                  <SortBtn field="totalCount"   label="Generations" />
                  <SortBtn field="totalCostUSD" label="Cost" />
                  <SortBtn field="totalTokens"  label="Tokens" />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-2.5 px-4 font-semibold text-gray-700 text-xs">User</th>
                      <th className="text-right py-2.5 px-4 font-semibold text-gray-700 text-xs">Generations</th>
                      <th className="text-right py-2.5 px-4 font-semibold text-gray-700 text-xs">Cost (USD)</th>
                      <th className="text-right py-2.5 px-4 font-semibold text-gray-700 text-xs">
                        Avg $/1K tokens
                        <span className="block font-normal text-gray-400 normal-case tracking-normal">(explains cost diff)</span>
                      </th>
                      <th className="text-right py-2.5 px-4 font-semibold text-gray-700 text-xs">Tokens</th>
                      <th className="text-right py-2.5 px-4 font-semibold text-gray-700 text-xs">Edited</th>
                      <th className="text-right py-2.5 px-4 font-semibold text-gray-700 text-xs">Edit %</th>
                      <th className="py-2.5 px-4 text-xs"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map((row) => {
                      const uid = String(row.user?._id || row._id);
                      const isOpen = expanded.has(uid);
                      const editPct = row.totalCount ? ((row.totalEdited / row.totalCount) * 100).toFixed(1) : '0.0';
                      const sortedTypes = [...(row.byType || [])].sort((a, b) => b.count - a.count);
                      const avgPer1K = row.totalTokens > 0
                        ? (row.totalCostUSD / row.totalTokens * 1000).toFixed(5)
                        : '0.00000';
                      const providers = (row.providers || []).filter(Boolean).sort();
                      const models    = (row.models    || []).filter(Boolean).sort();

                      return (
                        <React.Fragment key={uid}>
                          <tr className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isOpen ? 'bg-primary-50/30' : ''}`}>
                            <td className="py-3 px-4">
                              <p className="font-medium text-gray-900">{row.user?.name || '—'}</p>
                              <p className="text-xs text-gray-400 mb-1">{row.user?.email || ''}</p>
                              {providers.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {providers.map((p) => (
                                    <span key={p} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${
                                      p === 'claude'  ? 'bg-violet-100 text-violet-700' :
                                      p === 'openai'  ? 'bg-emerald-100 text-emerald-700' :
                                      p === 'gemini'  ? 'bg-blue-100 text-blue-700' :
                                      p === 'mistral' ? 'bg-orange-100 text-orange-700' :
                                      p === 'cohere'  ? 'bg-pink-100 text-pink-700' :
                                                        'bg-gray-100 text-gray-600'
                                    }`}>{p}</span>
                                  ))}
                                </div>
                              )}
                              {models.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {models.map((m) => (
                                    <span key={m} className="text-[10px] font-mono text-gray-400 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">{m}</span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-gray-800">{fmt(row.totalCount)}</td>
                            <td className="py-3 px-4 text-right font-medium text-gray-800">{fmtCost(row.totalCostUSD)}</td>
                            <td className="py-3 px-4 text-right">
                              <span className={`text-xs font-mono font-semibold ${
                                parseFloat(avgPer1K) > 0.005 ? 'text-red-600' :
                                parseFloat(avgPer1K) > 0.001 ? 'text-amber-600' :
                                                                'text-green-600'
                              }`}>${avgPer1K}</span>
                            </td>
                            <td className="py-3 px-4 text-right text-gray-600">{fmtK(row.totalTokens)}</td>
                            <td className="py-3 px-4 text-right text-gray-600">{fmt(row.totalEdited)}</td>
                            <td className="py-3 px-4 text-right">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                parseFloat(editPct) > 20 ? 'bg-green-100 text-green-700'
                                : parseFloat(editPct) > 5  ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-500'
                              }`}>{editPct}%</span>
                            </td>
                            <td className="py-3 px-4">
                              <button onClick={() => toggleExpand(uid)}
                                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
                                {isOpen ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
                                {isOpen ? 'Hide' : 'Types'}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded type breakdown */}
                          {isOpen && (
                            <tr className="bg-primary-50/20 border-b border-gray-100">
                              <td colSpan={8} className="px-6 pb-3 pt-2">
                                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Type Breakdown</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {sortedTypes.map(({ type, count, costUSD, tokens, edited, providers: typeProviders }) => (
                                    <div key={type} className="bg-white border border-gray-200 rounded-lg px-3 py-2">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-semibold text-gray-700">{toLabel(type)}</span>
                                        <span className="text-xs font-bold text-gray-900">{fmt(count)}</span>
                                      </div>
                                      <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
                                        <span>{fmtCost(costUSD)}</span>
                                        <span>{fmtK(tokens)} tokens</span>
                                        {edited > 0 && <span className="text-amber-600">{edited} edited</span>}
                                      </div>
                                      {(typeProviders || []).filter(Boolean).length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1.5">
                                          {(typeProviders || []).filter(Boolean).sort().map((p) => (
                                            <span key={p} className={`text-[9px] font-semibold px-1 py-0.5 rounded uppercase tracking-wide ${
                                              p === 'claude'  ? 'bg-violet-100 text-violet-700' :
                                              p === 'openai'  ? 'bg-emerald-100 text-emerald-700' :
                                              p === 'gemini'  ? 'bg-blue-100 text-blue-700' :
                                              p === 'mistral' ? 'bg-orange-100 text-orange-700' :
                                              p === 'cohere'  ? 'bg-pink-100 text-pink-700' :
                                                                'bg-gray-100 text-gray-600'
                                            }`}>{p}</span>
                                          ))}
                                        </div>
                                      )}
                                      {/* Mini usage bar */}
                                      <div className="mt-1.5 h-1 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                          className="h-full bg-primary-400 rounded-full"
                                          style={{ width: `${Math.min(100, (count / row.totalCount) * 100)}%` }}
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>

                  {/* Grand total row */}
                  <tfoot>
                    <tr className="bg-gray-900 text-white">
                      <td className="py-3 px-4 font-bold text-sm">Grand Total</td>
                      <td className="py-3 px-4 text-right font-bold">{fmt(report.totals.count)}</td>
                      <td className="py-3 px-4 text-right font-bold">{fmtCost(report.totals.costUSD)}</td>
                      <td className="py-3 px-4 text-right font-mono text-gray-300 text-xs">
                        ${report.totals.tokens > 0 ? (report.totals.costUSD / report.totals.tokens * 1000).toFixed(5) : '0.00000'}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-gray-300">{fmtK(report.totals.tokens)}</td>
                      <td className="py-3 px-4 text-right font-semibold text-gray-300">{fmt(report.totals.edited)}</td>
                      <td className="py-3 px-4 text-right font-semibold text-gray-300">
                        {report.totals.count ? ((report.totals.edited / report.totals.count) * 100).toFixed(1) : '0.0'}%
                      </td>
                      <td className="py-3 px-4" />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Type totals summary */}
              {Object.keys(report.totals.byType).length > 0 && (
                <div className="px-4 py-4 border-t border-gray-100">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Total by Content Type</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {Object.entries(report.totals.byType)
                      .sort(([, a], [, b]) => b.count - a.count)
                      .map(([type, data]) => (
                        <div key={type} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-semibold text-gray-700 truncate pr-1">{toLabel(type)}</span>
                            <span className="text-xs font-bold text-gray-900 flex-shrink-0">{fmt(data.count)}</span>
                          </div>
                          <p className="text-[11px] text-gray-500">{fmtCost(data.costUSD)} · {fmtK(data.tokens)}t</p>
                          {/* Proportion bar */}
                          <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-500 rounded-full"
                              style={{ width: `${Math.min(100, (data.count / report.totals.count) * 100)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Main History component ───────────────────────────────────────────────────
export const History = () => {
  const t = useT();
  const { user } = useAuthStore();
  // isAdmin gates the Report sub-tab (aggregate stats) — stays admin/superadmin-only.
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  // canViewTeamHistory gates the Details tab's multi-user view (user column + name
  // filter): admins see everyone, managers see other managers + editors (not admins
  // or plain users) — see ContentController.getHistory for the matching backend scope.
  const isManager = user?.role === 'manager';
  const canViewTeamHistory = isAdmin || isManager;
  const [adminTab, setAdminTab] = useState('details'); // 'details' | 'report'

  return (
    <div className="space-y-5">
      {/* Admin sub-tabs */}
      {isAdmin && (
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
          <button
            onClick={() => setAdminTab('details')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              adminTab === 'details' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiList size={14} /> Details
          </button>
          <button
            onClick={() => setAdminTab('report')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              adminTab === 'report' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiBarChart2 size={14} /> Report
          </button>
        </div>
      )}

      {/* Content */}
      {(!isAdmin || adminTab === 'details') && <DetailsTab t={t} canViewTeamHistory={canViewTeamHistory} />}
      {isAdmin && adminTab === 'report' && <ReportTab t={t} />}
    </div>
  );
};
